import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import * as htmlToImage from 'html-to-image';
import { createRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import LogoBlack from '@/images/logspot-black-black.svg?react';
import LogoWhite from '@/images/logspot-white-white.svg?react';
import { toast } from 'sonner';
import { Widget, type WidgetAction } from '@logspot-platform/widgets';
import { Button } from '../ui/button';

// The presentational widget shell now lives in @logspot-platform/widgets.
// This module keeps the admin-only chrome (dropdown menu + screenshot
// export) and re-exports the shell so existing `./widget` imports keep
// working.
export { Widget };

type ScreenshotModalProps = {
  Header?: () => React.ReactElement;
  children: ReactNode;
};

const takeScreenShot = async (node: HTMLElement) => {
  const dataURI = await htmlToImage.toJpeg(node, { skipFonts: true });
  return dataURI;
};

const download = (image: string, { name = 'img', extension = 'jpg' } = {}) => {
  const a = document.createElement('a');
  a.href = image;
  a.download = `${name}.${extension}`;
  a.click();
};

function createImage(options: { src?: string }) {
  options = options || {};
  const img = Image ? new Image() : document.createElement('img');
  if (options.src) {
    img.src = options.src;
  }
  return img;
}

function convertToPng(imgBlob: Blob) {
  const imageUrl = window.URL.createObjectURL(imgBlob);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const imageEl = createImage({ src: imageUrl });
  imageEl.onload = (e) => {
    if (ctx && e.target) {
      const img = e.target as HTMLImageElement;
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0, img.width, img.height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            copyToClipboard(blob);
          }
        },
        'image/png',
        1,
      );
    }
  };
}

async function copyImg(src: string) {
  const img = await fetch(src);
  const imgBlob = await img.blob();

  convertToPng(imgBlob);
}

async function copyToClipboard(pngBlob: Blob) {
  try {
    await navigator.clipboard.write([
      new window.ClipboardItem({
        [pngBlob.type]: pngBlob,
      }),
    ]);
  } catch (error) {
    console.error(error);
  }
}

const downloadScreenshot = (ref: RefObject<HTMLDivElement | null>) => {
  if (ref?.current) {
    takeScreenShot(ref.current)
      .then(download)
      .then(() => toast.success('Screenshot Downloaded'));
  }
};

const copyScreenshot = (ref: RefObject<HTMLDivElement | null>) => {
  if (ref?.current) {
    takeScreenShot(ref.current)
      .then(copyImg)
      .then(() => toast.success('Screenshot Copied'));
  }
};

const colors = [
  {
    bg: 'bg-primary',
    fg: 'text-white',
    logo: <LogoWhite className="h-7 w-auto" />,
  },
  {
    bg: 'bg-linear-to-bl from-gray-700 via-gray-900 to-black',
    fg: 'text-white',
    logo: <LogoWhite className="h-7 w-auto" />,
  },
  {
    bg: 'bg-linear-to-r from-indigo-200 via-red-200 to-yellow-100',
    fg: 'text-black',
    logo: <LogoBlack className="h-7 w-auto" />,
  },
  {
    bg: 'bg-linear-to-r from-orange-400 to-rose-400',
    fg: 'text-white',
    logo: <LogoWhite className="h-7 w-auto" />,
  },
  {
    bg: 'bg-linear-to-r from-gray-100 to-gray-300',
    fg: 'text-black',
    logo: <LogoBlack className="h-7 w-auto" />,
  },
];

const ScreenshotModal = ({ Header, children }: ScreenshotModalProps) => {
  const ref = createRef<HTMLDivElement>();
  const [bg, setBg] = useState(0);
  const colorConfig = colors[bg] ?? colors[0]!;

  return (
    <DialogContent className="p-10">
      <div
        ref={ref}
        className={`relative flex max-w-full origin-top items-center justify-center overflow-hidden ${colorConfig.bg}`}
      >
        <div className="mx-5 mt-5 mb-14 w-full shadow-lg md:mx-8 md:mt-10 md:mb-14 md:max-h-200">
          <Widget Header={Header}>{children}</Widget>
        </div>
        <div className="absolute right-8 bottom-3 flex items-center gap-2">
          <span className={`text-xs font-bold ${colorConfig.fg}`}>
            Powered by
          </span>
          {colorConfig.logo}
        </div>
      </div>
      <div className="mt-5 flex flex-col items-center justify-between gap-5 md:flex-row">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => downloadScreenshot(ref)}>
            Download
          </Button>
          <Button variant="outline" onClick={() => copyScreenshot(ref)}>
            Copy
          </Button>
        </div>
        <div className="flex items-center gap-1">
          {colors.map((c, index) => (
            <span
              key={index}
              className={`${c.bg} size-5 cursor-pointer rounded-full`}
              onClick={() => setBg(index)}
            ></span>
          ))}
        </div>
      </div>
    </DialogContent>
  );
};

/**
 * Admin chrome for the shared widget shell: the dropdown menu (Delete /
 * Hide / Screenshot) plus the screenshot export dialog. Mounted globally via
 * WidgetMenuProvider in main.tsx.
 */
export const WidgetMenu = ({
  actions,
  Header,
  children,
}: {
  actions: WidgetAction[];
  Header?: () => React.ReactElement;
  children?: ReactNode;
}) => {
  const [screenshotOpen, setScreenshotOpen] = useState(false);

  const allActions: WidgetAction[] = [
    ...actions,
    { label: 'Screenshot', action: () => setScreenshotOpen(true) },
  ];

  return (
    <Dialog
      open={screenshotOpen}
      onOpenChange={() => setScreenshotOpen(!screenshotOpen)}
    >
      <RowActionsMenu
        actions={allActions.map((action) => ({
          label: action.label,
          onSelect: action.action,
        }))}
      />
      {screenshotOpen && (
        <ScreenshotModal Header={Header}>{children}</ScreenshotModal>
      )}
    </Dialog>
  );
};
